import React, { FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
  Stack,
  Box,
  Tooltip,
  IconButton,
  Typography,
} from '@mui/material';

import { AlertConditionType, alertObjType, initialAlertCondition } from '../../../../features/alerts/types';
import { useTranslation } from 'react-i18next';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { postAlertConditions, putAlertConditions } from '../../../../features/alerts/requests';
import {
  alertConditionValidator,
  minimumValueForEachVariables,
} from '../../../../features/alerts/functions/alertConditionValidator';
import { AxiosError } from 'axios';
import { useSnackbar } from '../../../../components/hooks/useSnackbar';
import { useNavigate } from 'react-router-dom';
import { sendAnalyticsEvent } from '../../../../utils/googleAnalytics';

type Props = {
  selectedAlert: alertObjType | null;
};

const EditableTable: FC<Props> = ({ selectedAlert }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const isRiver = params.get('is_river') === 'true';
  const { t } = useTranslation();
  const [rows, setRows] = useState<AlertConditionType[]>([]);
  const navigate = useNavigate();
  const onCloseFunc = () => {
    navigate('/prediction/alerts/');
  };
  const { CustomSnackbar, setWindowAlert } = useSnackbar({ onCloseFunc });

  useEffect(() => {
    if (!!selectedAlert) setRows(selectedAlert?.conditions || []);
  }, [selectedAlert]);

  const handleAddRow = () => {
    // GA4: アラート条件行の追加を計測
    sendAnalyticsEvent('alert_condition', {
      action: 'add_row',
      is_river: isRiver,
    });
    if (isRiver) {
      setRows([
        ...rows,
        { ...initialAlertCondition, variable: 'waterLevel' },
      ]);
    } else {
      setRows([...rows, { ...initialAlertCondition }]);
    }
  };

  const updateRow = (index: number, name: keyof AlertConditionType, value: any) => {
    const newRows = [...rows];
    newRows[index][name] = value;
    setRows(newRows);
  };

  const handleInputChange =
    (index: number, name: keyof AlertConditionType) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      updateRow(index, name, event.target.value);
    };

  const handleSelectChange =
    (index: number, name: keyof AlertConditionType) => (event: SelectChangeEvent) => {
      updateRow(index, name, event.target.value as any);
    };

  const handleRemoveRow = (index: number) => {
      // GA4: アラート条件行の削除を計測
    sendAnalyticsEvent('alert_condition', {
      action: 'remove_row',
      is_river: isRiver,
    });
    setRows(rows.filter((_, rowIndex) => rowIndex !== index));
  };

  const handleSendClick = () => {
    if (!selectedAlert || !selectedAlert.id) {
      return;
    }
    const alertId = selectedAlert.id;
    const rowsWithId = rows.map(r => {
      return {
        alert: alertId,
        notification: r.notification,
        threshold: Number(r.threshold),
        timerange: Number(r.timerange),
        variable: r.variable,
      };
    });

    if (!alertId) {
      return;
    }

    if (!selectedAlert.conditions || selectedAlert.conditions.length < 1) {
      postAlertConditions(String(alertId), rowsWithId)
        .then(res => {
          // GA4: アラート条件の新規保存成功を計測
          sendAnalyticsEvent('alert_condition', {
            action: 'create_success',
            condition_count: rowsWithId.length,
            is_river: isRiver,
          });
          setWindowAlert(t('pages.alertList.conditionSetter.updateComplete'), 'success');
        })
        .catch(e => {
        });
      return;
    }
    putAlertConditions(String(alertId), rowsWithId).then(res => {
      if (res instanceof AxiosError) {
        setWindowAlert(t('pages.alertList.conditionSetter.updateFailed'), 'error');
        return;
      }
      // GA4: アラート条件の更新保存成功を計測
      sendAnalyticsEvent('alert_condition', {
        action: 'update_success',
        condition_count: rowsWithId.length,
        is_river: isRiver,
      });
      setWindowAlert(t('pages.alertList.conditionSetter.updateComplete'), 'success');
    });

    return;
  };

  const canSend = rows.every(r => {
    const res = alertConditionValidator(r);
    return res.isThresholdValid.isValid && res.isTimeRangeValid && res.isVariableFilled;
  });

  return (
    <Box>
      {CustomSnackbar}
      <TableContainer component={Paper} style={{ minWidth: '650px' }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t('pages.alertList.condition')}</TableCell>
              <TableCell align="right"></TableCell>
              <TableCell align="right">
                {t('pages.alertList.conditionSetter.notificationMethod')}
              </TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isTimeValid = Number(row.timerange) <= 36;
              const isThresholdValid = alertConditionValidator(row).isThresholdValid;
              const variable = isThresholdValid.variable;
              const minimumValueForTheVariable = minimumValueForEachVariables[variable];
              const validationMsg =
                t(`pages.alertList.conditionSetter.${variable}`) +
                String(
                  t(`pages.alertList.conditionSetter.validationMsg.${variable}`, {
                    thresh: minimumValueForTheVariable,
                  }),
                );

              return (
                <TableRow key={index}>
                  <TableCell style={{ paddingRight: '10px' }}>
                    <InputLabel id="demo-simple-select-label" style={{ fontSize: '9pt' }}>
                      {t('pages.alertList.conditionSetter.variable')}
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      name="variable"
                      value={row.variable}
                      onChange={handleSelectChange(index, 'variable')}
                      label={t('pages.alertList.conditionSetter.variable')}
                      variant="standard"
                      renderValue={(selected: string) => (
                        <div style={{ fontSize: '0.875rem' }}>
                          {t(`pages.alertList.conditionSetter.${selected}`)}
                        </div>
                      )}
                    >
                      <MenuItem value={'floodForecast'} disabled={isRiver}>
                        {t('pages.alertList.conditionSetter.floodForecast')}
                      </MenuItem>
                      <MenuItem value={'precipitation'} disabled>
                        {t('pages.alertList.conditionSetter.precipitation')}
                      </MenuItem>
                      <MenuItem value={'waterLevel'} disabled={!isRiver}>
                        {t('pages.alertList.conditionSetter.waterLevel')}
                      </MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right" style={{ padding: '8px' }}>
                    <Stack direction="row" spacing={0} style={{ alignItems: 'flex-end' }}>
                      <div style={{ fontSize: '10pt', paddingBottom: '6px' }}>
                        {t('pages.alertList.conditionSetter.in')}&nbsp;
                      </div>
                      <TextField
                        name="timerange"
                        value={row.timerange}
                        onChange={handleInputChange(index, 'timerange')}
                        label={t('pages.alertList.conditionSetter.timerange')}
                        variant="standard"
                        type="number"
                        inputProps={{
                          max: 36,
                          step: 1,
                          min: 0,
                        }}
                        error={!isTimeValid}
                      />
                      <div style={{ fontSize: '10pt', paddingBottom: '6px' }}>
                        {t('pages.alertList.conditionSetter.hours')}
                      </div>
                      <div style={{ marginLeft: '24px' }}>&nbsp;</div>
                      <TextField
                        name="threshold"
                        value={row.threshold}
                        onChange={handleInputChange(index, 'threshold')}
                        label={t('pages.alertList.conditionSetter.threshold')}
                        variant="standard"
                        type="number"
                        inputProps={{
                          max: 500,
                          step: 10,
                          min: 10,
                        }}
                        error={!isThresholdValid.isValid}
                      />
                      <div style={{ fontSize: '10pt', paddingBottom: '6px' }}>
                        {t('pages.alertList.conditionSetter.cm')}
                      </div>
                    </Stack>
                    <Stack style={{ textAlign: 'left' }}>
                      <div>
                        {!isTimeValid && (
                          <Typography style={{ fontSize: '9pt', color: 'red' }}>
                            {t('pages.alertList.conditionSetter.timerange')}
                            {t('pages.alertList.conditionSetter.validationMsg.timeRange')}
                          </Typography>
                        )}{' '}
                        {!isThresholdValid.isValid && (
                          <Typography style={{ fontSize: '9pt', color: 'red' }}>
                            {validationMsg}
                          </Typography>
                        )}
                      </div>
                    </Stack>
                  </TableCell>
                  <TableCell align="right">
                    <InputLabel id="notification" style={{ fontSize: '9pt' }}>
                      {t('pages.alertList.conditionSetter.notificationMethod')}
                      <Tooltip title={<pre>{t('pages.alertList.tooltip.notification')}</pre>}>
                        <HelpOutlineIcon
                          style={{ cursor: 'pointer', marginLeft: 2, fontSize: '14px' }}
                        />
                      </Tooltip>
                    </InputLabel>
                    <Select
                      labelId="notification"
                      name="notification"
                      value={row.notification}
                      onChange={handleSelectChange(index, 'notification')}
                      label={t('pages.alertList.conditionSetter.notificationMethod')}
                      variant="standard"
                    >
                      <MenuItem value={'mail'}>
                        {t('pages.alertList.conditionSetter.mail')}
                      </MenuItem>
                      <MenuItem value={'slack'} disabled>
                        {t('pages.alertList.conditionSetter.slack')}
                      </MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton aria-label="edit" onClick={() => handleRemoveRow(index)}>
                      <HighlightOffIcon style={{ fontSize: '14pt' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Button onClick={handleAddRow}>{t('pages.alertList.addCondition')}</Button>

      <Box style={{ marginTop: '36px' }}>
        <Button onClick={handleSendClick} variant="contained" disabled={!canSend}>
          {t('pages.alertList.save')}
        </Button>
      </Box>
    </Box>
  );
};

export default EditableTable;
